// Turning an .xlsx into a gTables workbook — the opinionated half.
//
// data/xlsx.ts reports what the file says. This decides what that MEANS here,
// and every judgement it makes is one of three:
//
//   1. A number format is a CODE in Excel and a name here, so `#,##0.00` has to
//      be recognised as "number, two decimals". The mapping is lossy in one
//      direction only: nothing invents precision the file did not have.
//   2. A formula is kept as a FORMULA. Excel also stores the last value it
//      calculated, and that is held aside so a formula this app cannot run can
//      fall back to the number the user last saw — see `demoteBrokenFormulas`.
//   3. Anything gTables has no concept of (hidden sheets, charts) is counted
//      and reported rather than silently dropped. Merged cells are a concept
//      gTables now has, so they are brought in rather than reported.

import type { Cell, CellValue, FormatId, Merge, Sheet, SheetRange, Workbook } from '@/model/types';
import {
  ck,
  createSheet,
  DEFAULT_COLS,
  DEFAULT_ROWS,
  setCellRaw,
  setCellValue,
  sheetByName,
  sheetById,
  uniqueSheetName,
  USAGES_SHEET_NAME,
} from '@/model/workbook';
import { parseRange } from '@/model/address';
import { Engine } from '@/engine/engine';
import type { Bytes } from './zip';
import { readXlsx, type XlsxSheet } from './xlsx';
import { fromExcelFormula } from './xlsxExport';
import { AREAS_SHEET_NAME } from './areasTable';
import { FEATURES_SHEET_NAME } from './featureTable';
import { BLOCKS_SHEET_NAME } from './blockTable';
import { ANALYTICS_SHEET_NAME } from './analyticsTable';

/**
 * gTables' own system tabs. A sheet in an IMPORTED file that happens to share
 * one of these names is never one of our system sheets — it is an ordinary
 * sheet from wherever the file came from — but letting it in under that name
 * is still wrong: `ensureSystemSheets` builds the real Areas/Features/etc.
 * fresh from the live project on every load, named via `uniqueSheetName`,
 * and a same-named import sheet already sitting there would steal the name
 * and push the REAL system sheet onto "Areas2" instead. Simplest fix is also
 * the right one: a sheet importing under a system name is dropped, not kept
 * under a different one — a Giraffe project does not have a stray "Areas2".
 */
const SYSTEM_SHEET_NAMES = new Set(
  [AREAS_SHEET_NAME, FEATURES_SHEET_NAME, BLOCKS_SHEET_NAME, USAGES_SHEET_NAME, ANALYTICS_SHEET_NAME].map((n) =>
    n.toLowerCase()
  )
);

/** Excel's 1904 date system runs this many days behind the 1900 one. */
const DATE_1904_OFFSET = 1462;

/**
 * The most cells one import may bring in.
 *
 * A workbook is saved into the Giraffe project on every change, and it is held
 * in memory as a plain object — so a 2-million-cell export is not a big
 * document, it is an app that never becomes usable again. The cap cuts the
 * BOTTOM off a sheet (cells arrive top row first) and says so, which is
 * recoverable; a hang is not.
 */
export const MAX_IMPORT_CELLS = 100_000;

/** A value Excel had already calculated, held against the formula cell it came from. */
export interface CachedValue {
  sheetId: string;
  row: number;
  col: number;
  value: CellValue;
  fmt?: FormatId;
}

export interface ImportReport {
  fileName: string;
  /** Sheet names as they landed, in workbook order. */
  sheets: string[];
  cells: number;
  formulas: number;
  /** Count of merged ranges that came in — informational, not a caveat. */
  merged: number;
  /** Sheets Excel had hidden. gTables has no hidden tab, so they are visible. */
  hidden: string[];
  /** Sheets cut short by MAX_IMPORT_CELLS. */
  truncated: string[];
  /** Sheets dropped because their name is one of gTables' own system tabs. */
  skippedSystemNames: string[];
  /** Named ranges that could not be resolved to a single rectangle on a sheet that came in. */
  skippedNamedRanges: string[];
}

export interface XlsxImport {
  workbook: Workbook;
  cached: CachedValue[];
  report: ImportReport;
}

export async function importXlsx(data: Bytes, fileName: string): Promise<XlsxImport> {
  return buildImport(await readXlsx(data), fileName);
}

export function buildImport(
  source: { sheets: XlsxSheet[]; date1904: boolean; definedNames?: { name: string; ref: string }[] },
  fileName: string
): XlsxImport {
  if (source.sheets.length === 0) throw new Error('That workbook has no worksheets in it.');

  const workbook: Workbook = { sheets: [], activeSheetId: '', blocks: {}, charts: {} };
  const cached: CachedValue[] = [];
  const report: ImportReport = {
    fileName,
    sheets: [],
    cells: 0,
    formulas: 0,
    merged: 0,
    hidden: [],
    truncated: [],
    skippedSystemNames: [],
    skippedNamedRanges: [],
  };
  let budget = MAX_IMPORT_CELLS;

  for (const from of source.sheets) {
    if (SYSTEM_SHEET_NAMES.has(from.name.trim().toLowerCase())) {
      report.skippedSystemNames.push(from.name);
      continue;
    }
    const sheet = createSheet(uniqueSheetName(workbook, from.name));
    workbook.sheets.push(sheet);
    report.sheets.push(sheet.name);
    report.merged += from.merged;
    if (from.hidden) report.hidden.push(sheet.name);

    sheet.colWidths = { ...from.colWidths };
    sheet.rowHeights = { ...from.rowHeights };
    if (Object.keys(from.hiddenRows).length > 0) sheet.hiddenRows = { ...from.hiddenRows };
    if (Object.keys(from.hiddenCols).length > 0) sheet.hiddenCols = { ...from.hiddenCols };
    // Excel's own panes, including none at all. A sheet that was not frozen
    // there should not arrive frozen here.
    sheet.freezeRows = from.freezeRows;
    sheet.freezeCols = from.freezeCols;
    sheet.rows = Math.max(DEFAULT_ROWS, from.rows + 20);
    sheet.cols = Math.max(DEFAULT_COLS, from.cols + 5);
    const merges = from.merges.filter((m) => m.r2 <= sheet.rows && m.c2 <= sheet.cols).map((m): Merge => ({ ...m }));
    if (merges.length > 0) sheet.merges = merges;

    for (const from2 of from.cells) {
      if (budget <= 0) {
        if (!report.truncated.includes(sheet.name)) report.truncated.push(sheet.name);
        break;
      }
      const fmt = formatFromCode(from2.numFmt);
      const value = adjustValue(from2.value, fmt, source.date1904);
      const cell: Cell = {};
      if (from2.formula !== undefined) {
        // `_xlfn.TEXTJOIN` is how the FILE spells TEXTJOIN; nothing here knows the prefix.
        cell.f = fromExcelFormula(from2.formula.startsWith('=') ? from2.formula.slice(1) : from2.formula);
        report.formulas++;
        // The cell holds the formula; the number Excel had goes on the side.
        if (value !== null) cached.push({ sheetId: sheet.id, row: from2.row, col: from2.col, value, fmt });
      } else if (value !== null) {
        cell.v = value;
      }
      if (fmt !== undefined) cell.fmt = fmt;
      if (from2.style !== undefined) cell.s = from2.style;
      if (Object.keys(cell).length === 0) continue;
      sheet.cells[ck(from2.row, from2.col)] = cell;
      report.cells++;
      budget--;
    }
  }

  if (workbook.sheets.length === 0) {
    throw new Error(
      `Every sheet in that workbook is named the same as one of gTables' own (${list(report.skippedSystemNames)}) — nothing left to import.`
    );
  }
  workbook.activeSheetId = workbook.sheets[0]!.id;

  // Resolved AFTER every sheet is in, because a name's ref points at a sheet
  // by the NAME it had in the file — which is what `workbook.sheets` now has.
  const namedRanges: Record<string, SheetRange> = {};
  for (const dn of source.definedNames ?? []) {
    // A multi-area ref (`Sheet1!$A$1,Sheet1!$B$1`) is not one rectangle, and
    // gTables has no concept of a named range that is more than one.
    const parsed = dn.ref.includes(',') ? null : parseRange(dn.ref);
    const target = parsed?.sheet ? sheetByName(workbook, parsed.sheet) : undefined;
    if (!parsed || !target) {
      report.skippedNamedRanges.push(dn.name);
      continue;
    }
    namedRanges[dn.name.toLowerCase()] = { sheetId: target.id, r1: parsed.r1, c1: parsed.c1, r2: parsed.r2, c2: parsed.c2 };
  }
  if (Object.keys(namedRanges).length > 0) workbook.namedRanges = namedRanges;

  return { workbook, cached, report };
}

/**
 * A date is a serial in both apps, so it needs no conversion — unless the
 * workbook uses the 1904 system, in which case every date in it is 1,462 days
 * out and nothing about the number itself says so.
 */
function adjustValue(value: CellValue, fmt: FormatId | undefined, date1904: boolean): CellValue {
  if (!date1904 || typeof value !== 'number') return value;
  if (fmt !== 'date' && fmt !== 'dateLong' && fmt !== 'monthYear') return value;
  return value + DATE_1904_OFFSET;
}

// ---------------------------------------------------------------------------
// Number formats
// ---------------------------------------------------------------------------

/**
 * An Excel format code to the nearest gTables format.
 *
 * The codes are a small language — literals in quotes, colours and locales in
 * brackets, up to four sections split by `;` for positive/negative/zero/text —
 * and gTables has twelve named formats. So this reads the code for what KIND of
 * thing it shows and how many decimals it shows it to, and drops the rest.
 *
 * Reading only the first section is deliberate: the negative and zero sections
 * are alternative faces for the same quantity, and nothing here can express
 * "red in brackets when negative" anyway.
 */
export function formatFromCode(code: string | undefined): FormatId | undefined {
  if (code === undefined) return undefined;
  const section = firstSection(code).trim();
  if (section === '' || /^general$/i.test(section)) return undefined;

  let text = false;
  let percent = false;
  let currency = false;
  let hasY = false;
  let hasD = false;
  let hasM = false;
  let longMonth = false;
  let hasTime = false;
  let numeric = false;
  let decimals = 0;
  let afterPoint = false;

  for (let i = 0; i < section.length; i++) {
    const ch = section[i]!;

    // A quoted literal is text Excel prints verbatim. Its LETTERS mean nothing
    // — `"Total"` is not a date — but its currency symbol does: the built-in
    // accounting formats write the symbol as `"$"`.
    if (ch === '"') {
      const end = section.indexOf('"', i + 1);
      const literal = section.slice(i + 1, end < 0 ? section.length : end);
      if (CURRENCY_RE.test(literal)) currency = true;
      i = end < 0 ? section.length : end;
      continue;
    }
    // `[Red]`, `[h]`, `[$€-407]` — only the currency locale carries meaning.
    if (ch === '[') {
      const end = section.indexOf(']', i + 1);
      const inner = section.slice(i + 1, end < 0 ? section.length : end);
      if (inner.startsWith('$')) currency = true;
      if (/^h+$|^m+$|^s+$/i.test(inner)) hasTime = true;
      i = end < 0 ? section.length : end;
      continue;
    }
    // `\$` and `_(` escape the next character out of the language.
    if (ch === '\\' || ch === '_' || ch === '*') {
      const next = section[i + 1];
      if (next && CURRENCY_RE.test(next)) currency = true;
      i++;
      continue;
    }

    if (CURRENCY_RE.test(ch)) {
      currency = true;
      continue;
    }
    switch (ch) {
      case '%':
        percent = true;
        continue;
      case '@':
        text = true;
        continue;
      case '.':
        afterPoint = true;
        continue;
      case '0':
      case '#':
      case '?':
        numeric = true;
        if (afterPoint) decimals++;
        continue;
      case ';':
        continue;
      default:
        break;
    }

    const lower = ch.toLowerCase();
    if (lower === 'y') hasY = true;
    else if (lower === 'd') hasD = true;
    else if (lower === 'm') {
      hasM = true;
      // `mmm` is a month name, `mm` a number, and `m` next to an `h` is
      // minutes. Counting the run is the only way to tell them apart.
      let run = 1;
      while (section[i + run]?.toLowerCase() === 'm') run++;
      if (run >= 3) longMonth = true;
      i += run - 1;
    } else if (lower === 'h' || lower === 's') hasTime = true;
  }

  if (text && !numeric) return 'text';

  const isDate = hasY || hasD || (hasM && !hasTime);
  if (isDate) {
    if (hasD) return longMonth ? 'dateLong' : 'date';
    if (hasY) return 'monthYear';
    return 'monthYear';
  }
  // A time — gTables has no clock format, and showing 0.375 as a date would be
  // a worse lie than showing it as the number it is.
  if (hasTime) return undefined;

  if (percent) return decimals >= 2 ? 'percent2' : decimals === 1 ? 'percent1' : 'percent0';
  if (currency) return decimals >= 2 ? 'currency2' : 'currency0';
  if (!numeric) return undefined;
  if (decimals >= 2) return 'number2';
  if (decimals === 1) return 'number1';
  // `#,##0` is "a number with separators"; a bare `0` is General with the
  // decimals pinned, and gTables' General already renders it that way.
  return section.includes(',') ? 'number0' : undefined;
}

const CURRENCY_RE = /[$£€¥₹₩]/;

/** The positive-number section, respecting quotes and brackets. */
function firstSection(code: string): string {
  for (let i = 0; i < code.length; i++) {
    const ch = code[i]!;
    if (ch === '"') {
      const end = code.indexOf('"', i + 1);
      i = end < 0 ? code.length : end;
    } else if (ch === '[') {
      const end = code.indexOf(']', i + 1);
      i = end < 0 ? code.length : end;
    } else if (ch === '\\') i++;
    else if (ch === ';') return code.slice(0, i);
  }
  return code;
}

// ---------------------------------------------------------------------------

/**
 * What the app can tell us about a formula, so the repair below can be reasoned
 * about without the engine being wired into the import.
 */
export interface FormulaProbe {
  /** What the workbook currently SHOWS at this cell. */
  valueAt(sheetId: string, row: number, col: number): CellValue | undefined;
  /** What this formula would come out as right now, writing nothing. */
  evaluate(sheetName: string, formula: string, row: number, col: number): CellValue;
  /** Recalculate, after cells have been rewritten. */
  recalculate(): void;
}

/** How many times a repair pass will chase a cascade before settling. */
const REPAIR_PASSES = 3;

/**
 * Put Excel's own number back into the formulas that will not run here.
 *
 * gTables keeps an imported formula AS a formula — a proforma that recalculates
 * is most of the reason to bring one in — but its parser is not Excel's. A
 * sheet built on XLOOKUP, a pivot reference or a link to another workbook would
 * otherwise arrive as a screen of #NAME?, which is strictly worse than the
 * numbers the user was looking at a minute ago. So a formula that errors falls
 * back to the value Excel cached for it.
 *
 * The subtlety, and the reason this is not one pass: an error SPREADS. One
 * unsupported COLUMN() at the top of a column makes every sum below it fail
 * too, and demoting those would kill a working model to fix one cell. So the
 * broken cells are demoted, the workbook is recalculated, and every demoted
 * cell is then offered its formula back — the ones that only failed because
 * something they read had failed now work, and stay live. A real acquisition
 * model went from 32 dead cells to 16 this way.
 *
 * A formula that was ALREADY broken in Excel keeps its formula: there is no
 * good number to fall back to, and hiding a #REF! the user needs to fix helps
 * nobody.
 *
 * Returns how many cells ended up holding a value instead of their formula.
 */
export function repairImportedFormulas(
  workbook: Workbook,
  cached: readonly CachedValue[],
  probe: FormulaProbe
): number {
  interface Demoted extends CachedValue {
    sheet: Sheet;
    formula: string;
    restored: boolean;
  }

  const demoted: Demoted[] = [];
  for (const entry of cached) {
    const sheet = sheetById(workbook, entry.sheetId);
    if (!sheet) continue;
    const formula = sheet.cells[ck(entry.row, entry.col)]?.f;
    if (formula === undefined) continue;
    if (!Engine.isError(probe.valueAt(entry.sheetId, entry.row, entry.col))) continue;
    if (Engine.isError(entry.value)) continue;
    setCellValue(sheet, entry.row, entry.col, entry.value, entry.fmt);
    demoted.push({ ...entry, sheet, formula, restored: false });
  }
  if (demoted.length === 0) return 0;
  probe.recalculate();

  let live = 0;
  for (let pass = 0; pass < REPAIR_PASSES; pass++) {
    let restored = 0;
    for (const cell of demoted) {
      if (cell.restored) continue;
      if (Engine.isError(probe.evaluate(cell.sheet.name, cell.formula, cell.row, cell.col))) continue;
      setCellRaw(cell.sheet, cell.row, cell.col, `=${cell.formula}`);
      cell.restored = true;
      restored++;
    }
    if (restored === 0) break;
    live += restored;
    probe.recalculate();
  }

  // Restoring one formula changes what the next one reads, so the settled
  // workbook is checked once more. Anything still broken goes back to its
  // number — this pass can only ever make the sheet better than one pass alone.
  for (const cell of demoted) {
    if (!cell.restored) continue;
    if (!Engine.isError(probe.valueAt(cell.sheetId, cell.row, cell.col))) continue;
    setCellValue(cell.sheet, cell.row, cell.col, cell.value, cell.fmt);
    cell.restored = false;
    live--;
  }
  if (live > 0) probe.recalculate();

  return demoted.length - live;
}

/**
 * What the user has to be told, or null when the file came in whole.
 *
 * Deliberately not a "3 sheets imported ✓" — the sheets appearing IS that
 * message, and an alert that always fires is one people learn to dismiss
 * without reading. This speaks only when something about the workbook is now
 * different from what they had in Excel.
 */
export function importCaveats(report: ImportReport, demoted: number): string | null {
  const notes: string[] = [];
  if (report.skippedSystemNames.length > 0) {
    notes.push(
      `${list(report.skippedSystemNames)} ${report.skippedSystemNames.length === 1 ? 'was' : 'were'} not imported — gTables builds its own sheet${report.skippedSystemNames.length === 1 ? '' : 's'} by that name from the live project.`
    );
  }
  if (report.truncated.length > 0) {
    notes.push(
      `Stopped at ${MAX_IMPORT_CELLS.toLocaleString()} cells — ${list(report.truncated)} came in only as far as that.`
    );
  }
  if (demoted > 0) {
    notes.push(
      `${count(demoted, 'formula')} could not be recalculated here, so ${demoted === 1 ? 'it holds' : 'they hold'} the value Excel last worked out.`
    );
  }
  if (report.hidden.length > 0) {
    notes.push(`${list(report.hidden)} ${report.hidden.length === 1 ? 'was' : 'were'} hidden in Excel, and ${report.hidden.length === 1 ? 'is' : 'are'} visible here.`);
  }
  if (report.skippedNamedRanges.length > 0) {
    notes.push(
      `${count(report.skippedNamedRanges.length, 'named range')} (${list(report.skippedNamedRanges)}) could not be brought in — a formula using ${report.skippedNamedRanges.length === 1 ? 'it' : 'one of them'} holds the value Excel last worked out instead.`
    );
  }
  if (notes.length === 0) return null;
  return `Imported ${count(report.sheets.length, 'sheet')} from ${report.fileName}.\n\n${notes.map((n) => `• ${n}`).join('\n')}`;
}

function count(n: number, noun: string): string {
  return `${n.toLocaleString()} ${noun}${n === 1 ? '' : 's'}`;
}

function list(names: readonly string[]): string {
  if (names.length <= 2) return names.join(' and ');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
